import { AnyObject, boolean, BooleanSchema, string, StringSchema } from 'yup';
import { Dictionary } from '@/src/lib/dictionary/types';
import { FormValidationModel } from '@/src/lib/validation/models/form-validation.model';
import { ValidationConfig } from '@/src/lib/validation/validation.config';
import { getMaxLengthMessage } from '@/src/lib/dictionary/utils';

interface RegisterFormValidation {
  salutation: StringSchema<string, AnyObject, undefined, ''>;
  firstName: StringSchema<string, AnyObject, undefined, ''>;
  lastName: StringSchema<string, AnyObject, undefined, ''>;
  email: StringSchema<string, AnyObject, undefined, ''>;
  country: StringSchema<string, AnyObject, undefined, ''>;
  customerNumber: StringSchema<string | undefined, AnyObject, undefined, ''>;
  phone: StringSchema<string, AnyObject, undefined, ''>;
  company: StringSchema<string, AnyObject, undefined, ''>;
  taxId: StringSchema<string | undefined, AnyObject, undefined, ''>;
  role: StringSchema<string, AnyObject, undefined, ''>;
  street: StringSchema<string, AnyObject, undefined, ''>;
  houseNumber: StringSchema<string, AnyObject, undefined, ''>;
  addressInformation: StringSchema<string | undefined, AnyObject, undefined, ''>;
  zipCode: StringSchema<string, AnyObject, undefined, ''>;
  city: StringSchema<string, AnyObject, undefined, ''>;
  acceptedTerms: BooleanSchema<NonNullable<boolean | undefined>, AnyObject, undefined, ''>;
  confirmTaxableEntity: BooleanSchema<NonNullable<boolean | undefined>, AnyObject, undefined, ''>;
}

export class RegisterFormValidationModel extends FormValidationModel implements RegisterFormValidation {
  constructor(dict: Dictionary) {
    super(ValidationConfig, dict);
  }

  public get salutation() {
    return string().required(this.REQUIRED_MESSAGE);
  }

  public get firstName() {
    const fieldName = this.DICT.userProfile.userData.firstName;
    const maxLength = 15;
    return string()
      .trim()
      .max(maxLength, getMaxLengthMessage(fieldName, maxLength, this.DICT))
      .required(this.REQUIRED_MESSAGE);
  }

  public get lastName() {
    const fieldName = this.DICT.userProfile.userData.lastName;
    const maxLength = 20;
    return string()
      .trim()
      .max(maxLength, getMaxLengthMessage(fieldName, maxLength, this.DICT))
      .required(this.REQUIRED_MESSAGE);
  }

  public get email() {
    return string().trim().email(this.EMAIL_REQUIRED_MESSAGE).required(this.REQUIRED_MESSAGE);
  }

  public get country() {
    return string().required(this.REQUIRED_MESSAGE);
  }

  public get customerNumber() {
    const fieldName = this.DICT.userProfile.userData.customerNumber;
    const maxLength = 10;
    return string()
      .trim()
      .max(maxLength, getMaxLengthMessage(fieldName, maxLength, this.DICT));
  }

  public get phone() {
    return string()
      .matches(ValidationConfig.PHONE_REGEX, this.DICT.messages.form.validation.phone)
      .required(this.REQUIRED_MESSAGE)
      .trim();
  }

  public get company() {
    const fieldName = this.DICT.userProfile.userData.companyName;
    const maxLength = 35;
    return string()
      .required(this.REQUIRED_MESSAGE)
      .trim()
      .max(maxLength, getMaxLengthMessage(fieldName, maxLength, this.DICT));
  }

  public get taxId() {
    const fieldName = this.DICT.userProfile.userData.companyTaxId;
    const maxLength = 20;
    return string()
      .trim()
      .max(maxLength, getMaxLengthMessage(fieldName, maxLength, this.DICT));
  }

  public get role() {
    return string().required(this.REQUIRED_MESSAGE);
  }

  public get street() {
    const fieldName = this.DICT.userProfile.userData.street;
    const maxLength = 60;
    return string()
      .required(this.REQUIRED_MESSAGE)
      .max(maxLength, getMaxLengthMessage(fieldName, maxLength, this.DICT))
      .trim();
  }

  public get addressInformation() {
    const fieldName = this.DICT.userProfile.userData.additionalAddressInformation;
    const maxLength = 35;
    return string()
      .max(maxLength, getMaxLengthMessage(fieldName, maxLength, this.DICT))
      .trim();
  }

  public get houseNumber() {
    const fieldName = this.DICT.userProfile.userData.houseNumber;
    const maxLength = 10;
    return string()
      .required(this.REQUIRED_MESSAGE)
      .trim()
      .max(maxLength, getMaxLengthMessage(fieldName, maxLength, this.DICT));
  }

  public get zipCode() {
    return string().required(this.REQUIRED_MESSAGE).trim();
  }

  public get city() {
    const fieldName = this.DICT.userProfile.userData.city;
    const maxLength = 40;
    return string()
      .required(this.REQUIRED_MESSAGE)
      .max(maxLength, getMaxLengthMessage(fieldName, maxLength, this.DICT))
      .trim();
  }

  public get region() {
    return string().nullable();
  }

  public get acceptedTerms() {
    return boolean().oneOf([true]).required(this.REQUIRED_MESSAGE);
  }

  public get confirmTaxableEntity() {
    return boolean().oneOf([true]).required(this.REQUIRED_MESSAGE);
  }

  public validate() {
    return {
      salutation: this.salutation,
      firstName: this.firstName,
      lastName: this.lastName,
      customerNumber: this.customerNumber,
      phone: this.phone,
      email: this.email,
      role: this.role,
      company: this.company,
      taxId: this.taxId,
      street: this.street,
      houseNumber: this.houseNumber,
      addressInformation: this.addressInformation,
      zipCode: this.zipCode,
      city: this.city,
      country: this.country,
      region: this.region,
      acceptedTerms: this.acceptedTerms,
      confirmTaxableEntity: this.confirmTaxableEntity,
    };
  }
}
